import type { RouteRecordRaw } from 'vue-router'
import type { Router } from 'vue-router'
import ToastEventBus from 'primevue/toasteventbus'
import { queryClient } from '@/api/queryClient'
import { getCurrentUser, getGetCurrentUserQueryKey } from '@/api/generated'
import type { CurrentUserDto } from '@/api/models/currentUserDto'
import { PlatformRole } from '@/api/models/platformRole'

declare module 'vue-router' {
  interface RouteMeta {
    requiresRoles?: PlatformRole[]
  }
}

export const routes: RouteRecordRaw[] = [
  {
    path: '/sign-in',
    name: 'sign-in',
    component: () => import('@/pages/sign-in/index.vue'),
  },
  {
    path: '/sign-up',
    name: 'sign-up',
    component: () => import('@/pages/sign-up/index.vue'),
  },
  {
    path: '/invite-accepted',
    name: 'invite-accepted',
    component: () => import('@/pages/invite-accepted/index.vue'),
  },
  {
    path: '/',
    component: () => import('@/layouts/DefaultLayout.vue'),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/pages/dashboard/index.vue'),
      },
      {
        path: 'dev',
        name: 'dev',
        component: () => import('@/pages/dev/index.vue'),
      },
      {
        path: 'dev/components',
        name: 'dev-components',
        component: () => import('@/pages/dev/component-showcase/index.vue'),
      },
      {
        path: 'dev/forms',
        name: 'dev-forms',
        component: () => import('@/pages/dev/forms/index.vue'),
      },
      {
        path: 'dev/vueuse',
        name: 'dev-vueuse',
        component: () => import('@/pages/dev/vueuse/index.vue'),
      },
      {
        path: 'dev/typography',
        name: 'dev-typography',
        component: () => import('@/pages/dev/typography/index.vue'),
      },
      {
        path: 'dev/people-seed',
        name: 'dev-people-seed',
        component: () => import('@/pages/dev/people-seed/index.vue'),
      },
      {
        path: 'people',
        name: 'people',
        component: () => import('@/pages/people/index.vue'),
        meta: { requiresRoles: [PlatformRole.HR] },
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('@/pages/users/index.vue'),
        meta: { requiresRoles: [PlatformRole.CompanyAdmin] },
      },
      {
        path: 'users/:id',
        name: 'users-detail',
        component: () => import('@/pages/users/[id]/index.vue'),
        meta: { requiresRoles: [PlatformRole.CompanyAdmin] },
      },
      {
        path: 'certifications',
        name: 'certifications',
        component: () => import('@/pages/certifications/index.vue'),
        meta: { requiresRoles: [PlatformRole.Compliance] },
      },
      {
        path: 'responsibilities',
        name: 'responsibilities',
        component: () => import('@/pages/responsibilities/index.vue'),
      },
      {
        path: 'direct-reports',
        name: 'direct-reports',
        component: () => import('@/pages/direct-reports/index.vue'),
        meta: { requiresRoles: [PlatformRole.LineManager] },
      },
      {
        path: 'my-certification',
        name: 'my-certification',
        component: () => import('@/pages/my-certification/index.vue'),
        meta: { requiresRoles: [PlatformRole.Employee] },
      },
      {
        path: 'organisation',
        name: 'organisation',
        component: () => import('@/pages/organisation/index.vue'),
        meta: { requiresRoles: [PlatformRole.CompanyAdmin] },
      },
      {
        path: 'organisational-functions',
        name: 'organisational-functions',
        component: () => import('@/pages/organisational-functions/index.vue'),
        meta: { requiresRoles: [PlatformRole.CompanyAdmin] },
      },
      {
        path: 'account',
        name: 'account',
        component: () => import('@/pages/account/index.vue'),
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/pages/settings/index.vue'),
        meta: { requiresRoles: [PlatformRole.CompanyAdmin] },
      },
    ],
  },
]

function waitForClerk(): Promise<typeof window.Clerk> {
  return new Promise((resolve) => {
    const check = () => {
      if (window.Clerk?.loaded) {
        resolve(window.Clerk)
      } else {
        setTimeout(check, 50)
      }
    }
    check()
  })
}

const SIGNED_OUT_ONLY_ROUTES = new Set(['sign-in', 'sign-up'])
const ALWAYS_PUBLIC_ROUTES = new Set(['invite-accepted'])

export function setupAuthGuard(router: Router) {
  router.beforeEach(async (to) => {
    const clerk = await waitForClerk()

    const isSignedIn = !!clerk?.user
    const name = typeof to.name === 'string' ? to.name : ''
    const isSignedOutOnly = SIGNED_OUT_ONLY_ROUTES.has(name)
    const isPublicRoute = isSignedOutOnly || ALWAYS_PUBLIC_ROUTES.has(name)

    if (!isSignedIn && !isPublicRoute) {
      return { name: 'sign-in' }
    }

    if (isSignedIn && isSignedOutOnly) {
      return { name: 'dashboard' }
    }

    const required = to.meta.requiresRoles
    if (!isSignedIn || !required || required.length === 0) return

    let userRoles: PlatformRole[] = []
    try {
      const me = await queryClient.fetchQuery<CurrentUserDto>({
        queryKey: getGetCurrentUserQueryKey(),
        queryFn: ({ signal }) => getCurrentUser(signal),
        retry: false,
      })
      userRoles = me.platformRoles ?? []
    } catch {
      // 404 means there is no linked Person — DefaultLayout swaps to
      // AccountErrorPage. Treat as no roles so the redirect-to-dashboard
      // rule fires and the user lands somewhere that can render the error.
    }

    const allowed = required.some((r) => userRoles.includes(r))
    if (allowed) return

    ToastEventBus.emit('add', {
      severity: 'warn',
      summary: 'Access denied',
      detail: "You don't have permission to view that page.",
      life: 3000,
    })
    return { name: 'dashboard' }
  })
}
